import { useEffect, useState } from "react";
import axios from "axios";
import { X, Loader2, FlaskConical, TrendingUp, TrendingDown, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const C = {
  bg: "rgba(0,0,0,0.78)",
  panel: "#09090B",
  panelElev: "#141416",
  border: "rgba(255,255,255,0.08)",
  text: "#FFFFFF",
  muted: "#71717A",
  cyan: "#22D3EE",
  violet: "#A78BFA",
  green: "#22FFA3",
  red: "#EF4444",
  amber: "#F59E0B",
};

/**
 * Modal per il backtest di un pattern Brain Lab sugli ultimi N giorni.
 * Props:
 *  - candidate: { keys, threshold, market, label_keys, label_market }
 *  - days: 30 (default)
 *  - onClose: () => void
 */
export default function BrainLabBacktestModal({ candidate, days = 30, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!candidate) return;
    setLoading(true);
    setError(null);
    axios
      .post(`${API}/brain-lab/backtest`, {
        keys: candidate.keys,
        threshold: candidate.threshold,
        market: candidate.market,
        days,
      })
      .then((res) => setData(res.data))
      .catch((err) =>
        setError(err?.response?.data?.detail || "Backtest fallito")
      )
      .finally(() => setLoading(false));
  }, [candidate, days]);

  if (!candidate) return null;

  const hr = data?.hit_rate;
  const base = data?.baseline;
  const edge = data?.edge_pp;
  const roi = data?.roi_pct;
  const hrColor = hr == null ? C.muted : hr >= 80 ? C.green : hr >= 65 ? C.cyan : hr >= 50 ? C.amber : C.red;
  const roiColor = roi == null ? C.muted : roi >= 15 ? C.green : roi >= 0 ? C.cyan : C.red;

  return (
    <div
      data-testid="brainlab-backtest-modal"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1100,
        background: C.bg,
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1.5rem",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.panel,
          border: `1px solid ${C.cyan}44`,
          boxShadow: `0 0 28px ${C.cyan}24`,
          maxWidth: 820,
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            height: 2,
            width: "100%",
            background: `linear-gradient(90deg, transparent, ${C.cyan}, ${C.violet}, transparent)`,
          }}
        />

        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.6rem",
            padding: "1rem 1.25rem",
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <FlaskConical size={14} style={{ color: C.cyan, filter: `drop-shadow(0 0 6px ${C.cyan})` }} />
          <span
            style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontWeight: 700,
              fontSize: "0.85rem",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: C.text,
            }}
          >
            Backtest · Ultimi {days} giorni
          </span>
          <button
            data-testid="brainlab-backtest-close"
            onClick={onClose}
            style={{
              marginLeft: "auto",
              background: "none",
              border: "none",
              color: C.muted,
              cursor: "pointer",
              display: "flex",
              padding: 4,
            }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Pattern header */}
        <div style={{ padding: "0.8rem 1.25rem", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            {(candidate.label_keys || []).map((lk, i) => (
              <span
                key={i}
                style={{
                  padding: "0.15rem 0.45rem",
                  background: `${C.cyan}14`,
                  border: `1px solid ${C.cyan}40`,
                  color: C.cyan,
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: "0.7rem",
                }}
              >
                {lk}
              </span>
            ))}
            <span style={{ color: C.muted, fontSize: "0.75rem" }}>≥{candidate.threshold}%</span>
            <span style={{ color: C.muted, fontSize: "0.85rem" }}>→</span>
            <span
              style={{
                padding: "0.15rem 0.5rem",
                background: `${C.violet}14`,
                border: `1px solid ${C.violet}40`,
                color: C.violet,
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.72rem",
                fontWeight: 700,
              }}
            >
              {candidate.label_market}
            </span>
          </div>
        </div>

        {/* Body */}
        {loading && (
          <div style={{ padding: "3rem", textAlign: "center", color: C.muted }}>
            <Loader2 size={20} className="animate-spin" style={{ color: C.cyan }} />
            <div style={{ marginTop: 12, fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem" }}>
              Sto rieseguendo il pattern su {days} giorni di partite reali...
            </div>
          </div>
        )}

        {error && !loading && (
          <div style={{ padding: "2rem", textAlign: "center", color: C.red, fontFamily: "'DM Sans',sans-serif" }}>
            ⚠ {error}
          </div>
        )}

        {data && !loading && (
          <div style={{ padding: "1.25rem" }}>
            {/* KPIs row */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.6rem", marginBottom: "1rem" }}>
              <KpiBox label="Hit rate" value={hr != null ? `${hr}%` : "—"} sub={`${data.hits}/${data.eligible_for_market} match`} color={hrColor} />
              <KpiBox label="Baseline" value={base != null ? `${base}%` : "—"} sub={`${data.baseline_n} match base`} color={C.muted} />
              <KpiBox label="Edge" value={edge != null ? `${edge >= 0 ? "+" : ""}${edge}pp` : "—"} sub={edge != null && edge >= 0 ? "VANTAGGIO" : "DEFICIT"} color={edge == null ? C.muted : edge >= 10 ? C.green : edge >= 0 ? C.cyan : C.red} icon={edge != null && edge >= 0 ? TrendingUp : TrendingDown} />
              <KpiBox label="ROI fair" value={roi != null ? `${roi >= 0 ? "+" : ""}${roi}%` : "—"} sub={data.quote_est ? `@ ~${data.quote_est}` : "no quota"} color={roiColor} />
            </div>

            {/* Recap riga */}
            <div
              style={{
                padding: "0.6rem 0.85rem",
                marginBottom: "1rem",
                background: C.panelElev,
                border: `1px solid ${C.border}`,
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.7rem",
                color: C.muted,
                display: "flex",
                gap: "1rem",
                flexWrap: "wrap",
              }}
            >
              <span>WINDOW <span style={{ color: C.text }}>{data.window_days}g</span></span>
              <span>MATCH CARICATI <span style={{ color: C.text }}>{data.rows_loaded}</span></span>
              <span>PASSING SOGLIA <span style={{ color: C.cyan }}>{data.passed_threshold}</span></span>
              <span>VALUTATI <span style={{ color: C.text }}>{data.eligible_for_market}</span></span>
            </div>

            {/* League breakdown */}
            {data.league_breakdown?.length > 0 && (
              <div style={{ marginBottom: "1rem" }}>
                <div
                  style={{
                    fontFamily: "'Barlow Condensed',sans-serif",
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    color: C.muted,
                    marginBottom: "0.5rem",
                  }}
                >
                  Top campionati nel campione
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {data.league_breakdown.map((lb, i) => (
                    <div
                      key={i}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 60px 70px 60px",
                        gap: "0.5rem",
                        padding: "0.35rem 0.6rem",
                        background: C.panelElev,
                        border: `1px solid ${C.border}`,
                        fontSize: "0.72rem",
                      }}
                    >
                      <span style={{ color: C.text, fontFamily: "'DM Sans',sans-serif" }}>{lb.league}</span>
                      <span style={{ color: C.muted, fontFamily: "'JetBrains Mono',monospace" }}>n={lb.n}</span>
                      <span style={{ color: lb.hit_rate >= 70 ? C.green : lb.hit_rate >= 50 ? C.cyan : C.amber, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>
                        {lb.hit_rate}%
                      </span>
                      <span style={{ color: C.muted, fontFamily: "'JetBrains Mono',monospace" }}>
                        {lb.hits}/{lb.n}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent outcomes timeline */}
            {data.recent_outcomes?.length > 0 && (
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontFamily: "'Barlow Condensed',sans-serif",
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    color: C.muted,
                    marginBottom: "0.5rem",
                  }}
                >
                  <Clock size={11} />
                  Ultime partite verificate
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {data.recent_outcomes.map((r, i) => (
                    <div
                      key={i}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "20px 1fr 60px 90px",
                        gap: "0.5rem",
                        padding: "0.3rem 0.55rem",
                        background: r.hit ? `${C.green}10` : `${C.red}10`,
                        border: `1px solid ${r.hit ? C.green + "33" : C.red + "33"}`,
                        fontSize: "0.7rem",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ color: r.hit ? C.green : C.red, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace" }}>
                        {r.hit ? "✓" : "✗"}
                      </span>
                      <span style={{ color: C.text, fontFamily: "'DM Sans',sans-serif" }}>
                        {r.home_team || "—"} <span style={{ color: C.muted }}>vs</span> {r.away_team || "—"}
                      </span>
                      <span style={{ color: C.text, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>
                        {r.score}
                      </span>
                      <span style={{ color: C.muted, fontSize: "0.62rem", fontFamily: "'DM Sans',sans-serif", textAlign: "right" }}>
                        {r.league}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Verdict */}
            <div
              style={{
                marginTop: "1rem",
                padding: "0.7rem 0.85rem",
                background: hr != null && hr >= 70 ? `${C.green}10` : `${C.amber}10`,
                border: `1px solid ${hr != null && hr >= 70 ? C.green + "55" : C.amber + "55"}`,
                fontFamily: "'DM Sans',sans-serif",
                fontSize: "0.78rem",
                color: hr != null && hr >= 70 ? C.green : C.amber,
                lineHeight: 1.5,
              }}
            >
              {hr == null
                ? "⚠ Nessun match valutabile nel periodo selezionato."
                : hr >= 75 && roi >= 10
                ? `✓ Pattern CONFERMATO: hit rate ${hr}% e ROI fair ${roi >= 0 ? "+" : ""}${roi}% confermano l'edge nel periodo recente.`
                : hr >= 60
                ? `⚡ Pattern decente: hit ${hr}% ma controlla ROI (${roi != null ? roi + "%" : "—"}) e baseline (${base}%) prima di usarlo come affidabile.`
                : `⚠ Pattern in REGRESSIONE: hit rate ${hr}% sotto soglia di sicurezza nel periodo recente. Cautela.`}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KpiBox({ label, value, sub, color, icon: Icon }) {
  return (
    <div
      style={{
        padding: "0.75rem 0.85rem",
        background: C.panelElev,
        border: `1px solid ${color}33`,
        boxShadow: `0 0 8px ${color}11`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          fontFamily: "'Barlow Condensed',sans-serif",
          fontWeight: 700,
          fontSize: "0.6rem",
          letterSpacing: "0.15em",
          textTransform: "uppercase",
          color: C.muted,
          marginBottom: 4,
        }}
      >
        {Icon && <Icon size={9} style={{ color }} />}
        {label}
      </div>
      <div
        style={{
          fontFamily: "'JetBrains Mono',monospace",
          fontWeight: 700,
          fontSize: "1.1rem",
          color,
          filter: `drop-shadow(0 0 4px ${color}55)`,
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: "0.6rem", color: C.muted, fontFamily: "'DM Sans',sans-serif", marginTop: 2 }}>
        {sub}
      </div>
    </div>
  );
}
